import { Badge, cn, Icon, IconButton, UiLink, type IconName } from '@nexus/ui';
import { useRouterState } from '@tanstack/react-router';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { visibleSections } from '../admin/sections';
import { useModules } from '../modules/context';
import type { NavModule } from '../modules/navigation';
import { useForMeCount, useShowForMe } from '../for-me/api';
import { useSession } from '../session/session';

export function isActivePath(current: string, path: string): boolean {
  return path === '/' ? current === '/' : current === path || current.startsWith(`${path}/`);
}

function NavItem({
  href,
  icon,
  label,
  accent,
  active,
  collapsed,
  nested = false,
  count,
  countLabel,
  onNavigate,
  trailing,
}: {
  href: string;
  icon: IconName;
  label: string;
  accent?: string;
  active: boolean;
  collapsed: boolean;
  nested?: boolean;
  /** Small number after the label, e.g. what waits for the person; `countLabel` reads it out. */
  count?: number;
  countLabel?: string;
  onNavigate?: () => void;
  /** Beside the link, e.g. the button that folds the pages of a module out. */
  trailing?: ReactNode;
}) {
  return (
    <li data-module-accent={accent} className={cn(trailing && 'flex items-center gap-0.5')}>
      <UiLink
        href={href}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        title={collapsed ? label : undefined}
        className={cn(
          'relative flex h-control min-w-0 flex-1 items-center gap-3 rounded-md px-2.5 text-fg-muted transition-colors transition-fast hover:bg-hover hover:text-fg',
          active && 'bg-brand-subtle font-medium text-fg hover:bg-brand-subtle',
          collapsed && 'justify-center px-0',
          nested && !collapsed && 'pl-9',
        )}
      >
        {active && !nested && (
          <span
            aria-hidden
            className="absolute top-2 bottom-2 -left-2 w-0.5 rounded-full bg-brand"
          />
        )}
        {!nested && (
          <Icon
            name={icon}
            className={cn('size-[1.125rem]', accent ? 'text-accent' : active ? 'text-brand' : '')}
          />
        )}
        <span className={cn('truncate', collapsed && 'sr-only')}>{label}</span>
        {count !== undefined && count > 0 && (
          <>
            <span aria-hidden className={cn('ml-auto flex', collapsed && 'absolute top-0 right-0')}>
              {/* On the active entry the brand tint is taken, so the number turns neutral. */}
              <Badge tone={active ? 'neutral' : 'brand'} className={cn(collapsed && 'h-4 px-1')}>
                {count > 99 ? '99+' : count}
              </Badge>
            </span>
            <span className="sr-only">{countLabel}</span>
          </>
        )}
      </UiLink>
      {trailing}
    </li>
  );
}

interface NavPage {
  path: string;
  icon: IconName;
  label: string;
  /** Small heading above the page, e.g. the area "Plattform" of the administration. */
  group?: string;
}

/**
 * An entry with pages, e.g. a module or the administration: the link to its start page and a
 * button that folds the pages out. The entry one is in starts open, the others closed, so the
 * menu on the phone stays short; the person's choice holds until the list is gone.
 */
function NavGroup({
  href,
  icon,
  label,
  accent,
  pages,
  collapsed,
  onNavigate,
}: {
  href: string;
  icon: IconName;
  label: string;
  accent?: string;
  pages: readonly NavPage[];
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const path = useRouterState({ select: (state) => state.location.pathname });
  const id = useId();
  const inside = isActivePath(path, href) || pages.some((page) => isActivePath(path, page.path));
  const [chosen, setChosen] = useState<boolean | null>(null);
  const foldable = !collapsed && pages.length > 1;
  const expanded = foldable && (chosen ?? inside);
  // Open, a page is marked instead, unless one is on a page outside the list, e.g. an overview.
  const onPage = pages.some((page) => isActivePath(path, page.path));

  return (
    <li>
      <ul className="flex flex-col gap-0.5">
        <NavItem
          href={href}
          icon={icon}
          label={label}
          accent={accent}
          active={inside && !(expanded && onPage)}
          collapsed={collapsed}
          onNavigate={onNavigate}
          trailing={
            foldable && (
              <IconButton
                icon="chevron-down"
                size="sm"
                label={t(expanded ? 'shell.collapseGroup' : 'shell.expandGroup', { name: label })}
                tooltip={false}
                aria-expanded={expanded}
                aria-controls={id}
                className={cn(
                  'shrink-0 text-fg-subtle transition-transform transition-fast',
                  !expanded && '-rotate-90',
                )}
                onClick={() => setChosen(!expanded)}
              />
            )
          }
        />
        {expanded && (
          <li>
            <ul id={id} className="flex flex-col gap-0.5">
              {pages.map((page, index) => (
                <PageEntry
                  key={page.path}
                  page={page}
                  heading={page.group !== pages[index - 1]?.group ? page.group : undefined}
                  active={isActivePath(path, page.path)}
                  onNavigate={onNavigate}
                />
              ))}
            </ul>
          </li>
        )}
      </ul>
    </li>
  );
}

function PageEntry({
  page,
  heading,
  active,
  onNavigate,
}: {
  page: NavPage;
  heading?: string;
  active: boolean;
  onNavigate?: () => void;
}) {
  const entry = (
    <NavItem
      onNavigate={onNavigate}
      href={page.path}
      icon={page.icon}
      label={page.label}
      active={active}
      collapsed={false}
      nested
    />
  );
  if (!heading) return entry;
  return (
    <>
      <li className="pt-2 pb-1 pl-9 text-xs font-medium text-fg-subtle">{heading}</li>
      {entry}
    </>
  );
}

/**
 * Home, "Für mich", the modules of the company with their pages, and the kernel areas the person
 * may open, the administration with its pages. Entries with pages fold out; the one the person is
 * in starts open, in the sidebar and in the menu on the phone alike.
 */
export function NavigationList({
  navigation,
  collapsed = false,
  onNavigate,
  className,
}: {
  navigation: readonly NavModule[];
  collapsed?: boolean;
  /** Called when an entry is chosen, e.g. to close the menu. */
  onNavigate?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const path = useRouterState({ select: (state) => state.location.pathname });
  const sections = visibleSections(useSession(), useModules());
  const showForMe = useShowForMe();
  const forMeCount = useForMeCount();
  const item = { collapsed, onNavigate };
  const areas = new Set(sections.map((section) => section.area));
  const adminPages: NavPage[] = sections.map((section) => ({
    path: section.path,
    icon: section.icon,
    label: t(`admin.sections.${section.id}.label`),
    // Headings only when both areas are there.
    group: areas.size > 1 ? t(`admin.areas.${section.area}`) : undefined,
  }));

  return (
    <nav aria-label={t('shell.navigation')} className={className}>
      <ul className="flex flex-col gap-0.5">
        <NavItem {...item} href="/" icon="house" label={t('shell.home')} active={path === '/'} />
        {showForMe && (
          <NavItem
            {...item}
            href="/for-me"
            icon="inbox"
            label={t('shell.forMe')}
            active={isActivePath(path, '/for-me')}
            count={forMeCount}
            countLabel={t('shell.forMeCount', { count: forMeCount })}
          />
        )}
      </ul>
      {navigation.length > 0 && (
        <>
          <h2
            className={cn(
              'px-2.5 pt-5 pb-1.5 text-xs font-medium text-fg-subtle',
              collapsed && 'sr-only',
            )}
          >
            {t('shell.modules')}
          </h2>
          <ul className="flex flex-col gap-0.5">
            {navigation.map((module) => (
              <NavGroup
                key={module.id}
                {...item}
                href={module.path}
                icon={module.icon}
                label={t(module.label)}
                accent={module.accent}
                pages={module.items.map((entry) => ({ ...entry, label: t(entry.label) }))}
              />
            ))}
          </ul>
        </>
      )}
      <ul className="mt-5 flex flex-col gap-0.5 border-t border-line pt-3">
        <NavItem
          {...item}
          href="/help"
          icon="book-open"
          label={t('shell.help')}
          active={isActivePath(path, '/help')}
        />
        {sections.length > 0 && (
          <NavGroup
            {...item}
            href="/admin"
            icon="settings"
            label={t('shell.admin')}
            pages={adminPages}
          />
        )}
      </ul>
    </nav>
  );
}

/** Navigation on tablet and desktop, collapsible to a bar of icons. */
export function Sidebar({
  navigation,
  collapsed,
  onToggle,
}: {
  navigation: readonly NavModule[];
  collapsed: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <aside
      className={cn(
        // Above the content, so nothing from the page can lie over the navigation and take its taps.
        'sticky top-(--topbar-h) z-30 hidden h-[calc(100dvh-var(--topbar-h))] shrink-0 flex-col border-r border-line bg-surface md:flex',
        collapsed ? 'w-16' : 'w-(--sidebar-w)',
      )}
    >
      <NavigationList
        navigation={navigation}
        collapsed={collapsed}
        className="flex-1 overflow-y-auto px-3 py-3"
      />
      <div className={cn('border-t border-line p-3', collapsed && 'flex justify-center')}>
        <IconButton
          icon={collapsed ? 'panel-left' : 'panel-left-close'}
          label={collapsed ? t('shell.expandSidebar') : t('shell.collapseSidebar')}
          onClick={onToggle}
          size="sm"
        />
      </div>
    </aside>
  );
}
